import { Link } from "react-router-dom";
import {ArrowRight, Layers} from 'lucide-react';
import { useSeoPages } from "@/hooks/useSeoPages";

type Silo = "BNSSA" | "MNS";

interface SiloDeepDiveProps {
  /** Univers courant (BNSSA ou MNS) */
  silo: Silo;
  /**
   * Path de la page courante (ex: "/bnssa/formations").
   * Sert à exclure la page elle-même de la liste des liens proposés.
   */
  currentPath?: string;
}

/**
 * Bloc "Approfondir le guide…" affiché en bas des pages silo
 * (offres d'emploi et formations BNSSA / MNS).
 *
 * Il liste les pages SEO enfants du pilier de l'univers (`/bnssa` ou
 * `/maitre-nageur-sauveteur`), en excluant les pages désactivées et la page
 * courante, et reprend exactement le design utilisé sur les pages piliers
 * (cf. SeoPage.tsx) pour conserver une cohérence visuelle parfaite.
 */
export default function SiloDeepDive({ silo, currentPath }: SiloDeepDiveProps) {
  const { mergedPages } = useSeoPages();

  const pillarPath = silo === "BNSSA" ? "/bnssa" : "/maitre-nageur-sauveteur";
  const isBNSSA = silo === "BNSSA";

  // Pages enfants du pilier, actives, hors page courante
  const safe = (mergedPages ?? []).filter(
    (p): p is NonNullable<typeof p> => Boolean(p)
  );
  const visible = safe.filter((p) => p?.isActive !== false);
  const children = visible.filter(
    (p) => p?.parent === pillarPath && p?.path !== currentPath
  );

  // Page pilier (pour le H2 et la phrase d'accroche)
  const pillar = safe.find((p) => p?.path === pillarPath);

  if (children.length === 0) return null;

  const accentText = isBNSSA ? "text-red-700" : "text-blue-700";
  const accentBorder = isBNSSA ? "border-red-200" : "border-blue-200";
  const sectionBg = isBNSSA ? "bg-red-50/40" : "bg-blue-50/40";
  const sectionBorder = isBNSSA ? "border-red-100" : "border-blue-100";

  // Nettoyage des excerpts (HTML → texte plat) cohérent avec SeoPage.tsx
  const cleanExcerpt = (raw: string): string =>
    raw
      .replace(/<[^>]+>/g, " ")
      .replace(/&nbsp;/gi, " ")
      .replace(/&amp;/gi, "&")
      .replace(/&quot;/gi, '"')
      .replace(/&#39;/gi, "'")
      .replace(/&apos;/gi, "'")
      .replace(/&laquo;/gi, "«")
      .replace(/&raquo;/gi, "»")
      .replace(/&lt;/gi, "<")
      .replace(/&gt;/gi, ">")
      .replace(/&hellip;/gi, "…")
      .replace(/&mdash;/gi, "—")
      .replace(/&ndash;/gi, "–")
      .replace(/\s+/g, " ")
      .trim();

  return (
    <section className={`py-14 sm:py-16 border-t ${sectionBg} ${sectionBorder}`}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-2 flex items-center gap-2">
          <Layers className={`w-6 h-6 ${accentText}`} />
          Approfondir le guide {silo}
        </h2>
        <p className="text-gray-600 mb-6">
          Thématiques détaillées pour explorer toutes les facettes du{" "}
          {pillar?.keyword || (isBNSSA ? "BNSSA" : "Maître Nageur Sauveteur")}.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {children.map((p) => (
            <Link
              key={p.id}
              to={p.path}
              className={`group bg-white border ${accentBorder} rounded-xl p-5 hover:shadow-lg transition-shadow`}
            >
              <div className="flex items-start justify-between gap-3 mb-2">
                <span
                  className={`text-xs font-bold uppercase tracking-wider ${accentText} capitalize`}
                >
                  {p.keyword}
                </span>
                <ArrowRight
                  className={`w-4 h-4 ${accentText} group-hover:translate-x-1 transition-transform flex-shrink-0`}
                />
              </div>
              <h3 className="font-bold text-gray-900 leading-snug mb-2 group-hover:underline">
                {p.h1}
              </h3>
              <p className="text-sm text-gray-600 line-clamp-2">
                {cleanExcerpt(p.intro ?? "")}
              </p>
            </Link>
          ))}
          {pillar && (
            <Link
              to={pillar.path}
              className={`group bg-white border ${accentBorder} rounded-xl p-5 hover:shadow-lg transition-shadow flex items-center justify-between gap-3`}
            >
              <div className="flex-1 min-w-0">
                <div className="text-xs text-gray-500 mb-0.5 capitalize">
                  Page mère
                </div>
                <div className="font-bold text-gray-900 text-sm">
                  {pillar.h1}
                </div>
              </div>
              <Layers className={`w-5 h-5 ${accentText} flex-shrink-0`} />
            </Link>
          )}
        </div>
      </div>
    </section>
  );
}
